import React, { useState, useCallback, useMemo, useRef } from 'react';
import { pick } from 'ramda';
import { type FormApi } from 'final-form';
import { Form } from 'js/components/common/Form';
import { Button } from 'js/components/common/Button';
import { Loader } from 'js/components/common/Loader';
import { Input } from 'js/components/common/Fields/Input';
import { Select } from 'js/components/common/Fields/Select';
import { Checkbox } from 'js/components/common/Fields/Checkbox';
import { ErrorMessage } from 'js/components/common/ErrorMessage';
import { useShowNotification } from 'js/components/common/Notifications';
import { useComponentTranslations } from 'js/hooks/component/useComponentTranslations';
import { DatePicker, convertISOToPicker, convertPickerToISO } from 'js/components/common/Fields/DatePicker';

import { SelectedFiles } from '../SelectedFiles';
import { DocumentLoadedBanner } from '../DocumentLoadedBanner';
import * as hooks from '../../hooks';
import * as helpers from '../../helpers';
import { useDynamicReducerSelector } from '../../store';
import * as Types from '../../types';

import style from './DocumentGroup.scss';

export const DocumentGroup: React.FC<Types.IDocumentGroupProps> = ({
    documentGroup,
    documentIndex
}) => {
    const formRef = useRef<FormApi<Types.IDocumentGroupFormValues> | null>(null);
    const translate = useComponentTranslations();
    const [files, setFiles] = useState<Types.IExtendedFile[]>([]);
    const showNotification = useShowNotification({ iconType: 'info' });
    const { tagsLoaded, playerTags } = useDynamicReducerSelector(pick(['tagsLoaded', 'playerTags']));
    const { playerTag, documentNumber, documentExpDate, documentType: rawDocumentType = '', limit = 1 } = documentGroup;
    const documentTypes = rawDocumentType
        .split(',')
        .map(documentType => documentType.trim())
        .filter(Boolean);

    const validate = hooks.useValidate(documentGroup);
    const uploadDocuments = hooks.useUploadDocuments(documentGroup);
    const onSubmit = useCallback(async (formValues: Types.IDocumentGroupFormValues): Promise<string | void> => {
        const errorMessage = await uploadDocuments(formValues, files);

        if (errorMessage) {
            return errorMessage;
        }

        formRef.current && formRef.current.restart();
        setFiles([]);
        showNotification({ message: translate('upload.success') });
    }, [uploadDocuments, files, setFiles, translate, showNotification]);
    const areFilesValid = useMemo((): boolean => Boolean(files.length) && files.length <= limit
        && files.every(file => helpers.isFileValid(files, file)
    ), [files, limit]);

    const initialValues = useMemo((): Partial<Types.IDocumentGroupFormValues> => ({
        documentType: documentTypes.length === 1 ? documentTypes[0] : '',
        documentNumber: '',
        documentExpDate: '',
        noExpDate: false
    }), [documentTypes]);

    const now = new Date();
    const yearNow = now.getFullYear();
    const range = `${30 + yearNow}-${yearNow}`;
    const allowPristineSubmit = !documentNumber && !documentExpDate && documentTypes.length < 2;

    if (!tagsLoaded) {
        return <Loader />;
    }

    if (playerTags.find(tag => tag.name === playerTag)) {
        return <DocumentLoadedBanner documentGroup={documentGroup} documentIndex={documentIndex} />;
    }

    return (
        <Form<Types.IDocumentGroupFormValues>
            onSubmit={onSubmit}
            validate={validate}
            validateOnBlur={true}
            initialValues={initialValues}
            render={({ form, values, handleSubmit, submitError, submitting, pristine }) => {
                formRef.current = form;

                return (
                    <form
                        className={style.form}
                        onSubmit={event => event.preventDefault()}
                    >
                        <h2 className={style.title}>
                            {translate(`documentIndex.${documentIndex > 2 ? 'other' : documentIndex}`)}
                        </h2>
                        {submitting && (<Loader />)}
                        {submitError && (
                            <ErrorMessage classNames={{ error: style.error }} error={submitError} animate={true} />
                        )}
                        {documentTypes.length > 0 && (
                            <Select
                                name="documentType"
                                classNames={{ control: style.control }}
                                label={translate('documentType.label')}
                                placeholder={translate('documentType.placeholder')}
                                aria-label={translate([
                                    'documentType.label',
                                    'documentType.placeholder',
                                    'playtech.mobile.system.input.aria-label'
                                ])}
                                options={[
                                    ...documentTypes.map(dt => ({
                                        value: dt,
                                        label: translate(`document.${dt}`)
                                    }))
                                ]}
                            />
                        )}
                        <SelectedFiles
                            files={files}
                            setFiles={setFiles}
                            documentGroup={documentGroup}
                            documentIndex={documentIndex}
                        />
                        {documentNumber && (
                            <Input
                                type="text"
                                name="documentNumber"
                                classNames={{ control: style.control }}
                                label={translate('documentNumber.label')}
                                placeholder={translate('documentNumber.placeholder')}
                                aria-label={translate([
                                    'documentNumber.label',
                                    'documentNumber.placeholder',
                                    'playtech.mobile.system.input.aria-label'
                                ])}
                            />
                        )}
                        {documentExpDate && (
                            <>
                                <DatePicker
                                    range={range}
                                    name="documentExpDate"
                                    disabled={values.noExpDate}
                                    classNames={{ control: style.control }}
                                    label={translate('documentExpDate.label')}
                                    aria-label={translate([
                                        'documentExpDate.label',
                                        'playtech.mobile.system.input.aria-label'
                                    ])}
                                    parse={convertPickerToISO}
                                    format={convertISOToPicker}
                                />
                                <Checkbox
                                    name="noExpDate"
                                    classNames={{ control: style.control }}
                                    label={translate('noExpDate.label')}
                                    aria-label={translate([
                                        'noExpDate.label',
                                        'playtech.mobile.system.input.aria-label'
                                    ])}
                                />
                            </>
                        )}
                        <Button
                            type="button"
                            className={style.button}
                            disabled={submitting || !areFilesValid || pristine && !allowPristineSubmit}
                            onClick={handleSubmit}
                        >
                            {translate('submit')}
                        </Button>
                    </form>
                );
            }}
        />
    );
};
